/**
 * storeWatch — ask the server, when the seller comes back to this tab, whether the account is still in
 * the store this editor was opened on (founder, 2026-09-17).
 *
 * ## Why a poll on FOCUS and not a message from the dashboard
 *
 * The dashboard knows the exact moment it switches store, and cannot tell us: it lives on `admin.<x>`,
 * the builder on `builder.<x>`. Different origins share no `BroadcastChannel`, no `storage` event and
 * no `localStorage`. The only thing both can see is the server, so the server is who we ask.
 *
 * ## Why on focus, and only then
 *
 * The switch happens in ANOTHER tab, which means this one was away while it happened and the seller
 * comes back to it to find the wrong shop. Coming back is therefore the exact instant the answer is
 * needed, and it costs one small query at a moment when nothing else is happening. A timer would ask
 * over and over for something that happens a handful of times a day; a check at boot would answer a
 * question we just minted a token for.
 *
 * `pageshow` with `persisted` is the same moment through a different door — the back/forward cache
 * restores a whole living document, in-memory token included, and it may remember no leaving of its
 * own, so that one asks unconditionally.
 *
 * ## What it must never do
 *
 * Break the editor. Every failure path — offline, a 500, a malformed body — leaves the builder exactly
 * as it was; a check that cannot answer says nothing rather than guessing. And it never runs in a
 * superadmin consultation: that token is a borrowed, read-only view of one store, `/api/auth/seller/*`
 * is not its surface, and there is no switcher anywhere near it.
 */
import { apiRequest } from '../lib/http';
import { isReadOnlyView } from './view-handoff';
import { isStoreDrifted, markStoreDrifted, pinnedStoreId } from './storePin';

/** One row of `GET /api/auth/seller/stores` (backend `StoreMembershipDto`), read defensively. */
interface StoreRow {
  readonly id?: unknown;
  readonly name?: unknown;
  readonly active?: unknown;
}

/**
 * ONLY ask after this tab has been away.
 *
 * The switch happens somewhere else — another tab, another window — so this tab was not in front when
 * it happened. Making "has been away" the precondition is both the cheapest rule and the most exact
 * one: the editor asks nothing at boot (it has just minted a token; the answer is already in hand) and
 * nothing while the seller works, and asks the moment she comes back, however fast that is.
 *
 * It replaced a plain 10-second throttle, which the e2e caught refusing to answer: a seller who opens
 * the builder, realises she is in the wrong shop, switches and comes back does the whole round trip in
 * a few seconds — and was told nothing, which is the exact defect this file exists to close.
 */
let wasAway = false;

/**
 * Coming back fires `visibilitychange` AND `focus` for one gesture. This floor collapses the pair into
 * one request; it is deliberately far too short to swallow a real trip to the dashboard and back.
 */
const COALESCE_MS = 1_500;

let lastCheckAt = 0;
let inFlight = false;
let installed = false;

/**
 * Ask which store the ACCOUNT is in now and latch the drift when it is not ours.
 *
 * The read goes out on THIS document's (possibly stale) token, which is the point: `active` is computed
 * from the `store_staff` rows of the token's SUBJECT, not from its `store_id` claim, so a token still
 * naming the store we left answers truthfully about the store we are now in.
 *
 * `force` asks even though the tab was never seen to leave — the bfcache restore, where the document
 * comes back from storage with no blur or hide of its own to remember. Never rejects.
 */
export async function checkActiveStore(force = false): Promise<void> {
  const pin = pinnedStoreId();
  // No pin (no store claim at boot — a seller who owns none yet), a consultation, already latched, or
  // one already on the wire: nothing to compare, nothing to say.
  if (pin === null || isReadOnlyView() || isStoreDrifted() || inFlight) return;
  if (!force && !wasAway) return;
  const now = Date.now();
  if (now - lastCheckAt < COALESCE_MS) return;
  wasAway = false;
  lastCheckAt = now;
  inFlight = true;
  try {
    const body = await apiRequest<{ stores?: readonly StoreRow[] }>('/api/auth/seller/stores');
    const active = body?.stores?.find((s) => s.active === true);
    const id = typeof active?.id === 'string' ? active.id : null;
    // No id means the answer did not arrive in the shape we read — silence, not a guess.
    if (id === null || id === pin) return;
    markStoreDrifted({ id, name: typeof active?.name === 'string' ? active.name : null });
  } catch {
    // Offline, 5xx, a 401 whose refresh failed — all of them leave the editor untouched. A refresh that
    // SUCCEEDS onto another store has already latched the drift inside withAuthRetry, so the throw that
    // arrives here is the one we wanted and the latch is set regardless.
  } finally {
    inFlight = false;
  }
}

/**
 * Install the focus/restore listeners. Idempotent (StrictMode double-mounts every effect) and returns
 * a teardown so a test — or a future unmount — can take them back off.
 */
export function installStoreWatch(): () => void {
  if (typeof window === 'undefined' || installed) return () => undefined;
  installed = true;

  const onVisible = (): void => {
    if (document.visibilityState === 'visible') void checkActiveStore();
    else wasAway = true;
  };
  const onFocus = (): void => void checkActiveStore();
  // Two ways to be away, and a seller uses both. Another TAB hides this document (`visibilitychange`);
  // another WINDOW — the dashboard side by side with the editor, a second screen — can leave it
  // `visible` and merely unfocused, and only `blur` sees that one.
  const onBlur = (): void => {
    wasAway = true;
  };
  const onPageShow = (event: PageTransitionEvent): void => {
    // Restored from the back/forward cache: the document, its JS memory and its token are all as old as
    // the last time it was on screen. Never throttled.
    if (event.persisted) void checkActiveStore(true);
  };

  document.addEventListener('visibilitychange', onVisible);
  window.addEventListener('focus', onFocus);
  window.addEventListener('blur', onBlur);
  window.addEventListener('pageshow', onPageShow);
  return () => {
    document.removeEventListener('visibilitychange', onVisible);
    window.removeEventListener('focus', onFocus);
    window.removeEventListener('blur', onBlur);
    window.removeEventListener('pageshow', onPageShow);
    installed = false;
  };
}

/** Test seam ONLY — this is module state and outlives a single test's clock. */
export function resetStoreWatchForTests(): void {
  lastCheckAt = 0;
  wasAway = false;
  inFlight = false;
  installed = false;
}

/** Test seam ONLY: stand in for the tab having been hidden or unfocused. */
export function markAwayForTests(): void {
  wasAway = true;
}
